import React, { createContext, useContext, useState } from "react";

const EmailContext = createContext();

export const useEmail = () => {
  const context = useContext(EmailContext);
  if (!context) {
    throw new Error("useEmail must be used within an EmailProvider");
  }
  return context;
};

export const EmailProvider = ({ children }) => {
  const [emails, setEmails] = useState([
    {
      id: 1,
      sender: "John Smith",
      senderEmail: "john.smith@example.com",
      subject: "Portfolio Review Meeting",
      preview:
        "I would like to schedule a meeting to discuss my investment portfolio.",
      time: "10:30 AM",
      isStarred: false,
      hasAttachment: true,
      unread: true,
      type: "Clients",
    },
    {
      id: 2,
      sender: "Sarah Johnson",
      senderEmail: "sarah.j@example.com",
      subject: "Investment Opportunity",
      preview:
        "I have been researching some potential investment opportunities that I would like to discuss.",
      time: "11:45 AM",
      isStarred: true,
      hasAttachment: false,
      unread: true,
      type: "Potential Clients",
    },
    {
      id: 3,
      sender: "Michael Chen",
      senderEmail: "michael.c@example.com",
      subject: "Quarterly Performance Update",
      preview:
        "Please find attached the quarterly performance report for your review.",
      time: "9:15 AM",
      isStarred: false,
      hasAttachment: true,
      unread: true,
      type: "Clients",
    },
    {
      id: 4,
      sender: "Emily Rodriguez",
      senderEmail: "emily.r@example.com",
      subject: "Tax Planning Strategy",
      preview:
        "I have some important updates regarding our tax planning approach.",
      time: "2:30 PM",
      isStarred: false,
      hasAttachment: false,
      unread: true,
      type: "Internal",
    },
    {
      id: 5,
      sender: "David Wilson",
      senderEmail: "d.wilson@example.com",
      subject: "Estate Planning Documents",
      preview:
        "The updated estate planning documents are ready for your review.",
      time: "3:45 PM",
      isStarred: true,
      hasAttachment: true,
      unread: true,
      type: "Clients",
    },
    {
      id: 6,
      sender: "Lisa Thompson",
      senderEmail: "l.thompson@example.com",
      subject: "Client Referral",
      preview: "I have a potential client who is interested in our services.",
      time: "11:20 AM",
      isStarred: false,
      hasAttachment: false,
      unread: true,
      type: "External",
    },
    {
      id: 7,
      sender: "Robert Brown",
      senderEmail: "r.brown@example.com",
      subject: "Investment Strategy Meeting",
      preview:
        "Can we schedule a meeting to discuss the new investment strategy?",
      time: "4:15 PM",
      isStarred: false,
      hasAttachment: false,
      unread: true,
      type: "Internal",
    },
    {
      id: 8,
      sender: "Jennifer White",
      senderEmail: "j.white@example.com",
      subject: "Account Update",
      preview:
        "Your account has been successfully updated with the new information.",
      time: "1:30 PM",
      isStarred: false,
      hasAttachment: false,
      unread: false,
      type: "Clients",
    },
  ]);

  const [selectedTypes, setSelectedTypes] = useState(
    new Set(["Clients", "Potential Clients", "Internal", "External"]),
  );

  const unreadCount = emails.filter((email) => email.unread).length;

  const markAsRead = (emailId) => {
    setEmails(
      emails.map((email) =>
        email.id === emailId ? { ...email, unread: false } : email,
      ),
    );
  };

  const markAllAsRead = () => {
    setEmails(emails.map((email) => ({ ...email, unread: false })));
  };

  const toggleStar = (emailId) => {
    setEmails(
      emails.map((email) =>
        email.id === emailId
          ? { ...email, isStarred: !email.isStarred }
          : email,
      ),
    );
  };

  const deleteEmails = (emailIds) => {
    setEmails(emails.filter((email) => !emailIds.includes(email.id)));
  };

  const toggleEmailType = (type) => {
    setSelectedTypes((prevTypes) => {
      const newTypes = new Set(prevTypes);
      if (type === "all") {
        if (newTypes.size === 4) {
          // All types are selected
          newTypes.clear();
        } else {
          newTypes.clear();
          ["Clients", "Potential Clients", "Internal", "External"].forEach(
            (t) => newTypes.add(t),
          );
        }
      } else {
        if (newTypes.has(type)) {
          newTypes.delete(type);
        } else {
          newTypes.add(type);
        }
      }
      return newTypes;
    });
  };

  const value = {
    emails,
    setEmails,
    unreadCount,
    selectedTypes,
    markAsRead,
    markAllAsRead,
    toggleStar,
    deleteEmails,
    toggleEmailType,
  };

  return (
    <EmailContext.Provider value={value}>{children}</EmailContext.Provider>
  );
};
